import { Icon } from '@iconify/react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import type { BadmintonGameListItem } from '@/services/badmintonService';
import {
  type SetScoreInput,
  isSet3Allowed,
} from '@/components/community/badminton/badmintonScoringUtils';

interface BadmintonSetScorePanelProps {
  game: BadmintonGameListItem;
  sets: SetScoreInput[];
  canEdit: boolean;
  onSetsChange: (sets: SetScoreInput[]) => void;
}

export default function BadmintonSetScorePanel({
  game,
  sets,
  canEdit,
  onSetsChange,
}: BadmintonSetScorePanelProps) {
  const set3Allowed = isSet3Allowed(game.maxSets, sets);

  const updateSet = (
    setNumber: number,
    patch: Partial<Pick<SetScoreInput, 'scoreSideA' | 'scoreSideB'>>
  ) => {
    if (!canEdit) return null;
    if (setNumber === 3 && !set3Allowed)
      return 'Set 3 aktif setelah masing-masing sisi menang 1 set';

    const next = sets.map((s) => {
      if (s.setNumber !== setNumber) return s;
      const scoreSideA = patch.scoreSideA ?? s.scoreSideA;
      const scoreSideB = patch.scoreSideB ?? s.scoreSideB;
      if (scoreSideA > 30 || scoreSideB > 30) return s;
      return { ...s, scoreSideA, scoreSideB };
    });

    if (patch.scoreSideA !== undefined && (patch.scoreSideA > 30 || patch.scoreSideA < 0)) {
      return 'Skor maksimal 30 poin per set';
    }
    if (patch.scoreSideB !== undefined && (patch.scoreSideB > 30 || patch.scoreSideB < 0)) {
      return 'Skor maksimal 30 poin per set';
    }

    onSetsChange(next);
    return null;
  };

  const bump = (setNumber: number, side: 'a' | 'b', delta: number) => {
    const row = sets.find((s) => s.setNumber === setNumber);
    if (!row) return;
    const val = (side === 'a' ? row.scoreSideA : row.scoreSideB) + delta;
    updateSet(setNumber, side === 'a' ? { scoreSideA: val } : { scoreSideB: val });
  };

  const setDirect = (setNumber: number, side: 'a' | 'b', raw: string) => {
    if (raw === '') return;
    const val = Number.parseInt(raw, 10);
    if (Number.isNaN(val)) return;
    updateSet(setNumber, side === 'a' ? { scoreSideA: val } : { scoreSideB: val });
  };

  const sideALabel = game.sideAPlayers.map((p) => p.playerName).join(' & ') || 'Sisi A';
  const sideBLabel = game.sideBPlayers.map((p) => p.playerName).join(' & ') || 'Sisi B';

  return (
    <div className="space-y-4">
      {game.maxSets >= 3 && !set3Allowed && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Set 3 akan aktif setelah Set 1 dan Set 2 dimenangkan oleh sisi berbeda.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {sets.map((set) => {
          const disabled = set.setNumber === 3 && !set3Allowed;
          return (
            <div
              key={set.setNumber}
              className={`rounded-xl border p-4 ${disabled ? 'opacity-50 bg-gray-50' : 'bg-white'}`}
            >
              <p className="text-center font-semibold text-gray-700 mb-3">Set {set.setNumber}</p>
              <div className="space-y-3">
                <ScoreRow
                  label={sideALabel}
                  value={set.scoreSideA}
                  disabled={disabled || !canEdit}
                  onMinus={() => bump(set.setNumber, 'a', -1)}
                  onPlus={() => bump(set.setNumber, 'a', 1)}
                  onInput={(v) => setDirect(set.setNumber, 'a', v)}
                />
                <ScoreRow
                  label={sideBLabel}
                  value={set.scoreSideB}
                  disabled={disabled || !canEdit}
                  onMinus={() => bump(set.setNumber, 'b', -1)}
                  onPlus={() => bump(set.setNumber, 'b', 1)}
                  onInput={(v) => setDirect(set.setNumber, 'b', v)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreRow({
  label,
  value,
  disabled,
  onMinus,
  onPlus,
  onInput,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onMinus: () => void;
  onPlus: () => void;
  onInput: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 truncate mb-1">{label}</p>
      <div className="flex items-center justify-center gap-2">
        <ShadcnButton
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          onClick={onMinus}
        >
          <Icon icon="mdi:minus" className="w-4 h-4" />
        </ShadcnButton>
        <Input
          className="w-16 text-center text-xl font-bold h-12"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onInput(e.target.value)}
        />
        <ShadcnButton
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          onClick={onPlus}
        >
          <Icon icon="mdi:plus" className="w-4 h-4" />
        </ShadcnButton>
      </div>
    </div>
  );
}
